/**
 * NOS-218: Single source of truth for resolving `org_customizations.footer_content`
 * into renderable footer data.
 *
 * Consumed by BOTH footer surfaces:
 *   - builder canvas: src/templates/sections/FooterSection.tsx
 *   - public site + preview: src/components/public/PublicFooter.tsx
 *
 * Any field the footer renders (tagline, quick_links, social_links, contact,
 * logo override) MUST be resolved here so the two surfaces cannot drift.
 */
import { Facebook, Instagram, Linkedin, Twitter, Youtube, type LucideIcon } from "lucide-react"
import { isDarkBackground, luminanceOf } from "@/lib/colorContrast"

/** The historical hardcoded footer background — still the default (NOS-217). */
export const DEFAULT_FOOTER_BACKGROUND = "#0f0f14"

export interface FooterQuickLink {
  label: string
  href: string
  open_in_new_tab?: boolean
}

export interface FooterSocialLink {
  label: string
  url: string
}

export interface FooterContactInfo {
  email?: string
  phone?: string
  address?: string
}

/**
 * NOS-217: foreground colors derived from the footer background's luminance so
 * text/links stay legible on imported light backgrounds. Alpha steps mirror
 * the original white-on-dark design (BUILDER_DESIGN_SPEC Section 15).
 */
export interface FooterForeground {
  /** Solid heading/org-name color (was #fff). */
  heading: string
  /** Root text color (was rgba(255,255,255,0.85)). */
  text: string
  /** Body/tagline/links (0.8). */
  body: string
  /** Contact rows (0.7). */
  muted: string
  /** Copyright (0.6). */
  subtle: string
  /** Social/"Powered by" (0.5). */
  faint: string
  /** Icon/empty-state (0.4). */
  icon: string
  /** Canvas placeholder hints (0.3). */
  hint: string
  /** Hairline borders (0.1). */
  border: string
  /** Stronger borders (0.2). */
  borderStrong: string
}

export function footerForeground(backgroundIsDark: boolean): FooterForeground {
  const base = backgroundIsDark ? "255,255,255" : "17,24,39"
  return {
    heading: backgroundIsDark ? "#ffffff" : "#111827",
    text: `rgba(${base},0.85)`,
    body: `rgba(${base},0.8)`,
    muted: `rgba(${base},0.7)`,
    subtle: `rgba(${base},0.6)`,
    faint: `rgba(${base},0.5)`,
    icon: `rgba(${base},0.4)`,
    hint: `rgba(${base},0.3)`,
    border: `rgba(${base},${backgroundIsDark ? "0.1" : "0.12"})`,
    borderStrong: `rgba(${base},0.2)`,
  }
}

/** Footer logo visibility mechanism — see ResolvedFooterContent.logoTreatment. */
export type FooterLogoTreatment = "knockout" | "chip"

/**
 * NOS-234: footer groups the user can move between the three columns.
 * The brand block (logo + tagline) is fixed in column 1.
 */
export type FooterMovableGroup = "quick_links" | "connect" | "contact" | "subscribe"

export type FooterColumnLayout = Record<FooterMovableGroup, 1 | 2 | 3>

export const DEFAULT_FOOTER_COLUMN_LAYOUT: FooterColumnLayout = {
  quick_links: 2,
  connect: 3,
  contact: 3,
  subscribe: 1,
}

/** NOS-232: imported footer newsletter block, re-wired to NOS email capture. */
export interface FooterSubscribeContent {
  heading: string
  body: string | null
  cta_text: string
  placeholder: string
}

/**
 * NOS-234: the footer's vertical padding. The section panel's
 * Compact/Normal/Spacious control writes `_styles.padding` on the footer
 * section content (mirrored into footer_content) — previously ignored by both
 * footer surfaces, leaving the control dead.
 */
export function footerVerticalPadding(
  footerContent: Record<string, unknown> | null | undefined
): string {
  const styles = footerContent?._styles as { padding?: string } | undefined
  if (styles?.padding === "compact") return "32px"
  if (styles?.padding === "spacious") return "96px"
  return "var(--nos-space-section-y, 64px)"
}

export interface ResolvedFooterContent {
  /** Footer-specific logo override (light variant for the dark background), else the org logo, else null (render the org name instead). */
  logoUrl: string | null
  /** True when logoUrl is the user/import-set footer_content.logo_url override (PR #240) — treatment never applies to it. */
  logoIsOverride: boolean
  /**
   * How the org logo is kept visible against the footer background when the
   * two would collide: "knockout" (default) renders the logo as a solid
   * white/black silhouette via CSS filter; "chip" keeps the full-color logo on
   * a contrasting chip. Opaque images always fall back to the chip (a filter
   * would flood their background rectangle).
   */
  logoTreatment: FooterLogoTreatment
  /**
   * NOS-217: footer background CSS color. `footer_content.background` when it
   * parses as a color (imported from the source site or set in the footer
   * panel), else DEFAULT_FOOTER_BACKGROUND.
   */
  background: string
  /** True when `background` is dark (white foreground wins on WCAG contrast). */
  backgroundIsDark: boolean
  /** Luminance-derived foreground palette for text/links/borders. */
  fg: FooterForeground
  /**
   * Tagline to render, or null to render nothing. `footer_content.tagline` wins;
   * the org mission statement is the fallback when the field is absent; the
   * `tagline_deleted`/`tagline_hidden` flags (set by canvas element delete)
   * suppress both.
   */
  tagline: string | null
  /**
   * Custom quick links. `null` means footer_content itself is absent
   * (pre-migration orgs) — the public footer auto-generates links from pages
   * in that case. An empty array means "footer_content exists with no links".
   */
  quickLinks: FooterQuickLink[] | null
  socialLinks: FooterSocialLink[]
  /** Contact block (NOS-188: email/phone/address parsed from an imported footer); null when no non-empty field exists. */
  contact: FooterContactInfo | null
  /**
   * NOS-234: which column each movable group renders in (brand is fixed in
   * column 1). `footer_content.column_layout` with per-key validation —
   * malformed or missing keys fall back to the default layout.
   */
  columnLayout: FooterColumnLayout
  /**
   * NOS-232: footer newsletter block imported from the source (or null).
   * Rendered as a live NOS email capture — never a dead form. Missing text
   * fields fall back to sensible defaults here so both surfaces agree.
   */
  subscribe: FooterSubscribeContent | null
}

export function normalizeUrl(url: string): string {
  const u = url.trim()
  if (!u) return ""
  if (u.startsWith("http://") || u.startsWith("https://")) return u
  return `https://${u}`
}

export function socialIconForLabel(label: string): LucideIcon | null {
  const l = label.toLowerCase()
  if (l.includes("facebook")) return Facebook
  if (l.includes("twitter") || l === "x") return Twitter
  if (l.includes("instagram")) return Instagram
  if (l.includes("linkedin")) return Linkedin
  if (l.includes("youtube")) return Youtube
  return null
}

/** Strip non-dial characters for tel: hrefs. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^+\d]/g, "")}`
}

export interface ResolveFooterContentOptions {
  /** Org-level logo (fallback when footer_content has no logo override). */
  orgLogoUrl?: string | null
  /** Org mission statement (tagline fallback when footer_content.tagline is absent). */
  missionStatement?: string | null
}

export function resolveFooterContent(
  footerContent: Record<string, unknown> | null | undefined,
  opts: ResolveFooterContentOptions = {}
): ResolvedFooterContent {
  const fc =
    footerContent && typeof footerContent === "object" && !Array.isArray(footerContent)
      ? (footerContent as Record<string, unknown>)
      : null

  // Logo: footer-specific override wins over the org logo.
  const overrideLogo =
    typeof fc?.logo_url === "string" && fc.logo_url.trim() ? fc.logo_url.trim() : null
  const orgLogo =
    typeof opts.orgLogoUrl === "string" && opts.orgLogoUrl.trim() ? opts.orgLogoUrl.trim() : null
  const logoUrl = overrideLogo ?? orgLogo
  const logoIsOverride = overrideLogo !== null
  const logoTreatment: FooterLogoTreatment = fc?.logo_treatment === "chip" ? "chip" : "knockout"

  // NOS-217: footer background — imported/user-set color, else the default dark.
  const rawBackground = typeof fc?.background === "string" ? fc.background.trim() : ""
  const background =
    rawBackground && luminanceOf(rawBackground) !== null ? rawBackground : DEFAULT_FOOTER_BACKGROUND
  const backgroundIsDark = isDarkBackground(background) ?? true
  const fg = footerForeground(backgroundIsDark)

  // Tagline: NO hardcoded default — an org without a tagline or mission shows nothing,
  // identically in canvas and preview.
  const taglineDeleted = fc?.tagline_deleted === true || fc?.tagline_hidden === true
  let tagline: string | null = null
  if (!taglineDeleted) {
    if (typeof fc?.tagline === "string") {
      // Field present (even empty after clearing): use it verbatim, no mission fallback.
      tagline = fc.tagline.trim() || null
    } else {
      const mission =
        typeof opts.missionStatement === "string" ? opts.missionStatement.trim() : ""
      tagline = mission || null
    }
  }

  // Quick links: footer_content exists → use whatever is stored (even empty).
  let quickLinks: FooterQuickLink[] | null = null
  if (fc) {
    const rawQL = fc.quick_links
    quickLinks = Array.isArray(rawQL)
      ? (rawQL
          .map((item) => {
            if (!item || typeof item !== "object") return null
            const o = item as Record<string, unknown>
            const label = typeof o.label === "string" ? o.label : ""
            const href = typeof o.href === "string" ? o.href : ""
            return label && href
              ? { label, href, open_in_new_tab: o.open_in_new_tab === true }
              : null
          })
          .filter(Boolean) as FooterQuickLink[])
      : []
  }

  // Social links
  const rawSocial = fc?.social_links
  const socialLinks = Array.isArray(rawSocial)
    ? (rawSocial
        .map((item) => {
          if (!item || typeof item !== "object") return null
          const o = item as Record<string, unknown>
          const label = typeof o.label === "string" ? o.label : "Social"
          const url = typeof o.url === "string" ? normalizeUrl(o.url) : ""
          return url ? { label, url } : null
        })
        .filter(Boolean) as FooterSocialLink[])
    : []

  // Contact (NOS-188)
  let contact: FooterContactInfo | null = null
  const rawContact = fc?.contact
  if (rawContact && typeof rawContact === "object" && !Array.isArray(rawContact)) {
    const o = rawContact as Record<string, unknown>
    const parsed: FooterContactInfo = {
      ...(typeof o.email === "string" && o.email.trim() ? { email: o.email.trim() } : {}),
      ...(typeof o.phone === "string" && o.phone.trim() ? { phone: o.phone.trim() } : {}),
      ...(typeof o.address === "string" && o.address.trim() ? { address: o.address.trim() } : {}),
    }
    if (Object.keys(parsed).length > 0) contact = parsed
  }

  // NOS-234: column assignments for the movable groups.
  const rawLayout = fc?.column_layout
  const columnLayout: FooterColumnLayout = { ...DEFAULT_FOOTER_COLUMN_LAYOUT }
  if (rawLayout && typeof rawLayout === "object" && !Array.isArray(rawLayout)) {
    for (const key of Object.keys(DEFAULT_FOOTER_COLUMN_LAYOUT) as FooterMovableGroup[]) {
      const v = (rawLayout as Record<string, unknown>)[key]
      if (v === 1 || v === 2 || v === 3) columnLayout[key] = v
    }
  }

  // NOS-232: imported footer newsletter block
  let subscribe: FooterSubscribeContent | null = null
  const rawSub = fc?.subscribe
  if (rawSub && typeof rawSub === "object" && !Array.isArray(rawSub)) {
    const o = rawSub as Record<string, unknown>
    const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "")
    subscribe = {
      heading: str(o.heading) || "Stay in touch",
      body: str(o.body) || null,
      cta_text: str(o.cta_text) || "Subscribe",
      placeholder: str(o.placeholder) || "Enter your email",
    }
  }

  return {
    logoUrl,
    logoIsOverride,
    logoTreatment,
    background,
    backgroundIsDark,
    fg,
    tagline,
    quickLinks,
    socialLinks,
    contact,
    columnLayout,
    subscribe,
  }
}
